import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import { Alert, Button, Card, Input, Space, Typography } from 'antd';
import { useState } from 'react';
import { useValuesCommand } from '../values/useValuesCommand';

/**
 * E36 · PublishValuesDeck (buildOrder 1) — "Backoffice — Values decks — Publish" (cc-tools-values, chapter "Values — Personal values
 * card sort (mobile, E36)", screen e36e0001-0001-4000-8000-000000000001): the deck the phone's card sort offers. The Miller deck is
 * seeded by the operator's CLI through this same command; a known deck ID revises it (the latest publication wins). Catalogue
 * content, no person. Members only (the server's gate: `isBackofficeMember`). A language missing on any card is refused by the
 * server (DECK_TRANSLATION_MISSING) with its copy.
 */
type DeckView = {
  deckId: string;
  name: string;
  licence: string;
  citation: string;
  language: string;
  cards: Array<{ card: string; text: string }>;
  presets: Array<{ preset: string; cards: number; cardIds: string[] }>;
};

const DECK_FIELDS = [
  'deckId',
  'name',
  'licence',
  'citation',
  'language',
  { cards: ['card', 'text'] },
  { presets: ['preset', 'cards', 'cardIds'] },
];

export const BackofficeValuesDecks = () => {
  const getLocale = useGetLocale();
  const locale: 'en' | 'ru' = getLocale()?.startsWith('ru') ? 'ru' : 'en';
  const t = useTranslate() as unknown as (
    k: string,
    o?: Record<string, unknown>,
  ) => string;
  const { run, isLoading } = useValuesCommand();
  const { data, isError, refetch } = useCustom<DeckView>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'valuesDeck',
      fields: DECK_FIELDS,
      variables: {
        language: { name: 'language', type: 'String', value: locale },
      },
    },
  });
  const deck = (data?.data ?? null) as DeckView | null;
  const [deckId, setDeckId] = useState('');
  const [name, setName] = useState('');
  const [licence, setLicence] = useState('');
  const [citation, setCitation] = useState('');
  const [cardsJson, setCardsJson] = useState('[]');
  const [presetsJson, setPresetsJson] = useState('[]');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const ready =
    /^[A-Za-z][A-Za-z0-9_-]{1,48}$/.test(deckId.trim()) &&
    name.trim() &&
    licence.trim() &&
    citation.trim();

  const publish = async () => {
    let cards: unknown;
    let presets: unknown;
    try {
      cards = JSON.parse(cardsJson);
      presets = JSON.parse(presetsJson);
    } catch {
      setMessage({ type: 'error', text: t('backoffice.valuesDecks.notJson') });
      return;
    }
    const res = await run(
      'publishValuesDeck',
      {
        deckId: deckId.trim(),
        name: name.trim(),
        cards,
        presets,
        languages: ['en', 'ru'],
        licence: licence.trim(),
        citation: citation.trim(),
      },
      t('backoffice.valuesDecks.failed'),
    );
    if (res.ok) {
      setMessage({
        type: 'success',
        text: t('backoffice.valuesDecks.published'),
      });
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="backoffice-values-decks"
    >
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.valuesDecks.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.valuesDecks.intro')}
        </Typography.Paragraph>
      </div>
      {isError ? (
        <Alert
          type="error"
          showIcon
          message={t('backoffice.valuesDecks.loadFailed')}
        />
      ) : null}
      {message ? (
        <Alert
          type={message.type}
          showIcon
          message={message.text}
          closable
          onClose={() => setMessage(null)}
          data-testid={`backoffice-values-decks-message-${message.type}`}
        />
      ) : null}
      <Card
        size="small"
        title={t('backoffice.valuesDecks.current')}
        data-testid="backoffice-values-decks-current"
      >
        {deck ? (
          <Space direction="vertical" size={0}>
            <Typography.Text strong data-testid="backoffice-values-decks-name">
              {deck.deckId} · {deck.name}
            </Typography.Text>
            <span data-testid="backoffice-values-decks-count">
              {t('backoffice.valuesDecks.cards', { count: deck.cards.length })}
            </span>
            <span>
              {t('backoffice.valuesDecks.presets', {
                presets: deck.presets
                  .map((p) => `${p.preset} (${p.cards})`)
                  .join(' · '),
              })}
            </span>
            <Typography.Text type="secondary">{deck.citation}</Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">
            {t('backoffice.valuesDecks.empty')}
          </Typography.Text>
        )}
      </Card>
      <Card size="small" title={t('backoffice.valuesDecks.formTitle')}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            addonBefore={t('backoffice.valuesDecks.deckId')}
            data-testid="backoffice-values-decks-id"
            onChange={(e) => setDeckId(e.target.value)}
            value={deckId}
          />
          <Input
            addonBefore={t('backoffice.valuesDecks.name')}
            data-testid="backoffice-values-decks-name-input"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
          <Input
            addonBefore={t('backoffice.valuesDecks.licence')}
            data-testid="backoffice-values-decks-licence"
            onChange={(e) => setLicence(e.target.value)}
            value={licence}
          />
          <Input
            addonBefore={t('backoffice.valuesDecks.citation')}
            data-testid="backoffice-values-decks-citation"
            onChange={(e) => setCitation(e.target.value)}
            value={citation}
          />
          <Typography.Text>
            {t('backoffice.valuesDecks.cardsJson')}
          </Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 12 }}
            data-testid="backoffice-values-decks-cards"
            onChange={(e) => setCardsJson(e.target.value)}
            value={cardsJson}
          />
          <Typography.Text>
            {t('backoffice.valuesDecks.presetsJson')}
          </Typography.Text>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 8 }}
            data-testid="backoffice-values-decks-presets"
            onChange={(e) => setPresetsJson(e.target.value)}
            value={presetsJson}
          />
          <div>
            <Button
              type="primary"
              disabled={!ready}
              loading={isLoading}
              onClick={() => {
                publish().catch(() => undefined);
              }}
              data-testid="backoffice-values-decks-publish"
            >
              {t('backoffice.valuesDecks.publish')}
            </Button>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
